// src/app/scheduling/pages/user-assign-schedules/user-assign-schedules.component.ts
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Observable, of, Subject, forkJoin } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import dayjs from 'dayjs';
import { RouterModule } from '@angular/router';

import { SchedulingNavComponent } from '../../../shared/scheduling-nav/scheduling-nav.component';
import { UserAssignSchedulesService } from '../../services/user-assign-schedules.service';
import {
  AssignedScheduleBlock,
  CreateUserAssignedScheduleRequest,
  DepartmentListItem,
  EmployeeWeekSchedules,
  ScheduleGroupDetails,
  ScheduleGroupListItem,
  ShiftListItem,
  YywwListItem,
} from '../../models/user-assign-schedules.model';
import { FilterService } from '../../../services/filter.service';

type WeekdayKey = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';
type InlineDragMode = 'move' | 'resizeStart' | 'resizeEnd';
type ToastKind = 'success' | 'error' | 'info';
type SaveEditMode = 'single' | 'next' | 'applyAll';

type BulkAssignScope =
  | 'templateSelectedDay'
  | 'templateSelectedWeek'
  | 'manualSelectedDay'
  | 'manualSelectedDays'
  | 'manualSelectedWeek';

interface WeekDayVm {
  key: WeekdayKey;
  label: string;
  date: Date;
  ymd: string;
}

interface TemplateDragPayload {
  kind: 'template';
  scheduleGroupId: number;
}

interface ExistingDragPayload {
  kind: 'existing';
  assignmentId: number;
  employeeId: number;
}

type DragPayload = TemplateDragPayload | ExistingDragPayload;

interface ContextMenuState {
  open: boolean;
  x: number;
  y: number;
  employeeId: number;
  ymd: string;
  assignmentId?: number;
}

interface EditDraft {
  employeeId: number;
  ymd: string;
  startTime: string;
  endTime: string;
  isOvernight: boolean;
  departmentId: number | null;
  shiftId: number | null;
  sourceAssignmentId?: number;
  sourceScheduleGroupId?: number;
}

interface BulkAssignDraft {
  title: string;
  scope: BulkAssignScope;
  employeeIds: number[];
  selectedDayIdx: boolean[];
  startTime: string;
  endTime: string;
  isOvernight: boolean;
  departmentId: number | null;
  shiftId: number | null;
  sourceScheduleGroupId?: number;
  currentYmd?: string | null;
  currentDayLabel?: string | null;
  timeZoneId?: string | null;
}

interface TemplatePreviewRow {
  dayLabel: string;
  timeRange: string;
  isOvernight: boolean;
}

interface InlineEditDraft {
  assignmentId: number;
  employeeId: number;
  originalStart: Date;
  originalEnd: Date;
  start: Date;
  end: Date;
  departmentId: number | null;
  shiftId: number | null;
}

interface QuickAddDraft {
  employeeId: number;
  startTime: string;
  endTime: string;
  departmentId: number | null;
  shiftId: number | null;
  selectedDayIdx: boolean[];
}

interface TemplateDayPlan {
  startTime: string;
  endTime: string;
  isOvernight: boolean;
  shiftId: number | null;
  shiftName: string | null;
  timeZoneId: string | null;
}

interface BulkTemplateQueueItem {
  ymd: string;
  dayLabel: string;
  startTime: string;
  endTime: string;
  isOvernight: boolean;
  shiftId: number | null;
  timeZoneId: string | null;
  employeeIds: number[];
  sourceScheduleGroupId: number;
  scope: 'templateSelectedDay' | 'templateSelectedWeek';
}

type EditQueueMode = 'none' | 'singleTemplate' | 'bulkTemplate';

@Component({
  selector: 'app-user-assign-schedules',
  standalone: true,
  imports: [CommonModule, FormsModule, SchedulingNavComponent, RouterModule],
  templateUrl: './user-assign-schedules.component.html',
  styleUrls: ['./user-assign-schedules.component.scss'],
})
export class UserAssignSchedulesComponent implements OnInit, AfterViewInit, OnDestroy {
  weekSchedules$: Observable<EmployeeWeekSchedules[]> = of([]);
  private weekSchedulesSnapshot: EmployeeWeekSchedules[] = [];

  isLoadingWeek = true;
  isLoadingWeeks = true;
  isLoadingScheduleGroups = false;

  errorMessage: string | null = null;

  selectedYear = new Date().getFullYear();
  years: number[] = [];
  weeks: YywwListItem[] = [];
  selectedWeek: YywwListItem | null = null;

  weekDays: WeekDayVm[] = [];

  scheduleGroups: ScheduleGroupListItem[] = [];

  sidebarOpen = true;
  private scheduleGroupsLoaded = false;
  filterService = inject(FilterService);

  private employeeById = new Map<number, EmployeeWeekSchedules>();
  private blockByAssignmentId = new Map<number, AssignedScheduleBlock>();

  selectedEmployeeIds = new Set<number>();

  contextMenu: ContextMenuState = {
    open: false,
    x: 0,
    y: 0,
    employeeId: 0,
    ymd: '',
  };

  editDraft: EditDraft | null = null;
  bulkAssignDraft: BulkAssignDraft | null = null;

  isSaving = false;
  isBulkSaving = false;

  quickAddDraft: QuickAddDraft | null = null;
  isQuickAddSaving = false;

  deleteConfirmOpen = false;
  deleteTarget: {
    assignmentId: number;
    employeeId: number;
    ymd: string;
    label: string;
  } | null = null;
  isDeleting = false;

  private templateApplyQueue: EditDraft[] = [];
  private templateApplyActive = false;

  private bulkTemplateApplyQueue: BulkTemplateQueueItem[] = [];
  private bulkTemplateApplyActive = false;

  private editQueueMode: EditQueueMode = 'none';
  private editQueueTotalCount = 0;
  private editQueueCurrentIndex = 0;
  private editQueueEmployeeIds: number[] = [];
  private editQueueTitle: string | null = null;

  private dragPayload: DragPayload | null = null;
  isTemplateDragging = false;
  hoverEmployeeId: number | null = null;
  hoverDayIndex: number | null = null;
  isSelectedDropzoneHover = false;
  hoverSelectedEmployeesDayIndex: number | null = null;

  private expandedTemplateIds = new Set<number>();
  private templatePreviewCache = new Map<number, TemplatePreviewRow[]>();
  private scheduleGroupDetailsInflight = new Map<number, Promise<ScheduleGroupDetails | null>>();
  private scheduleGroupDetailsMemory = new Map<number, ScheduleGroupDetails>();
  private loadingTemplateDetailsIds = new Set<number>();

  @ViewChild('timelineGrid', { static: false }) private timelineGridRef?: ElementRef<HTMLElement>;
  private resizeObserver: ResizeObserver | null = null;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'success';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  private timelineScrollEl: HTMLElement | null = null;

  @ViewChild('timelineScroll')
  set timelineScroll(ref: ElementRef<HTMLElement> | undefined) {
    const next = ref?.nativeElement ?? null;
    if (this.timelineScrollEl === next) return;

    this.unbindHorizontalPanHandlers();
    this.timelineScrollEl = next;
    this.bindHorizontalPanHandlers();
  }

  inlineEditAssignmentId: number | null = null;
  private inlineDrafts = new Map<number, InlineEditDraft>();

  private activePointerId: number | null = null;
  private activeMode: InlineDragMode | null = null;
  private activeAssignmentId: number | null = null;
  private laneRect: DOMRect | null = null;

  private dragStartClientX = 0;
  private dragStartStartMin = 0;
  private dragStartEndMin = 0;

  private readonly WEEK_MINUTES = 7 * 1440;
  private readonly SNAP_MINUTES = 5;
  private readonly MIN_DURATION_MINUTES = 15;

  private hoverClearRaf: number | null = null;

  private isPanning = false;
  private panPointerId: number | null = null;
  private panStartX = 0;
  private panStartScrollLeft = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly service: UserAssignSchedulesService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get isTemplateApplyActive(): boolean {
    return this.templateApplyActive;
  }

  get isBulkTemplateApplyActive(): boolean {
    return this.bulkTemplateApplyActive;
  }

  get canApplyAll(): boolean {
    return this.templateApplyActive || this.bulkTemplateApplyActive;
  }

  get modalPrimaryLabel(): string {
    return this.templateApplyActive || this.bulkTemplateApplyActive ? 'Save and Next Day' : 'Save';
  }

  get canDeleteContextSchedule(): boolean {
    return !!this.contextMenu.open && !!this.contextMenu.assignmentId;
  }

  get hasInlineChanges(): boolean {
    return this.inlineDrafts.size > 0;
  }

  get selectedWeekIndex(): number {
    if (!this.selectedWeek?.id) return -1;
    return this.weeks.findIndex((w) => w.id === this.selectedWeek!.id);
  }

  get weekLabel(): string {
    const w = this.selectedWeek;
    if (!w) return '';

    const start = dayjs(w.startDate, 'YYYY-MM-DD', true);
    const end = dayjs(w.endDate, 'YYYY-MM-DD', true);

    if (!start.isValid() || !end.isValid()) return '';
    return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
  }

  get quickAddSelectedCount(): number {
    return (this.quickAddDraft?.selectedDayIdx ?? []).filter(Boolean).length;
  }

  get bulkAssignSelectedEmployeeCount(): number {
    return this.bulkAssignDraft?.employeeIds.length ?? 0;
  }

  get bulkAssignSelectedDayCount(): number {
    return (this.bulkAssignDraft?.selectedDayIdx ?? []).filter(Boolean).length;
  }

  get bulkAssignEmployeeSummary(): string {
    const draft = this.bulkAssignDraft;
    if (!draft) return '';

    const count = draft.employeeIds.length;
    if (count === 1) {
      return this.getEmployeeDisplayName(draft.employeeIds[0]);
    }

    return `${count} selected employees`;
  }

  get editModalTitle(): string {
    if (this.editQueueMode === 'bulkTemplate') {
      return this.editQueueTitle ?? 'Assign Template';
    }

    if (this.editQueueMode === 'singleTemplate') {
      return this.editQueueTitle ?? 'Assign Template';
    }

    return 'Edit Schedule';
  }

  get editQueueProgressText(): string {
    if (!this.editDraft || this.editQueueMode === 'none' || this.editQueueTotalCount <= 1) {
      return '';
    }

    return `Editing day ${this.editQueueCurrentIndex} of ${this.editQueueTotalCount}`;
  }

  get editQueueContextText(): string {
    if (!this.editDraft) return '';

    if (this.editQueueMode === 'bulkTemplate') {
      const count = this.editQueueEmployeeIds.length;
      return `${count} selected employee${count === 1 ? '' : 's'} • ${this.editDraft.ymd}`;
    }

    if (this.editQueueMode === 'singleTemplate') {
      return `Template assignment • ${this.editDraft.ymd}`;
    }

    return '';
  }

  get showSingleEmployeeInEditModal(): boolean {
    return !!this.editDraft && this.editQueueMode !== 'bulkTemplate';
  }

  get showMultiEmployeeSummaryInEditModal(): boolean {
    return !!this.editDraft && this.editQueueMode === 'bulkTemplate';
  }

  get editModalSelectedEmployeeCount(): number {
    return this.editQueueEmployeeIds.length;
  }

  get showEditApplyAllButton(): boolean {
    return this.templateApplyActive || this.bulkTemplateApplyActive;
  }

  get editApplyAllButtonTitle(): string {
    if (this.bulkTemplateApplyActive) {
      return 'Apply this Department + Shift to all remaining template days for all selected employees';
    }

    if (this.templateApplyActive) {
      return 'Apply this Department + Shift to all remaining template days';
    }

    return 'Apply to all';
  }

  get selectedTemplateHasNoWeekDays(): boolean {
    const details = this.getDraggedTemplateDetailsSync();
    if (!details) return false;
    return !this.weekDays.some((d) => this.hasTemplateScheduleForDay(details, d.ymd));
  }

  ngOnInit(): void {
    const nowYear = new Date().getFullYear();
    this.years = Array.from({ length: 11 }, (_, i) => nowYear - 5 + i);

    if (this.sidebarOpen) {
      void this.ensureScheduleGroupsLoaded();
    }

    void this.loadWeeksForYear(this.selectedYear, new Date());
  }

  ngAfterViewInit(): void {
    const root = this.timelineGridRef?.nativeElement;
    if (root && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {});
      this.resizeObserver.observe(root);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    if (this.hoverClearRaf != null) {
      cancelAnimationFrame(this.hoverClearRaf);
      this.hoverClearRaf = null;
    }

    this.unbindHorizontalPanHandlers();
    this.timelineScrollEl = null;
    this.clearToast();
  }

  trackByEmployeeId(_: number, row: EmployeeWeekSchedules): number {
    return row.id;
  }

  trackByWeekId(_: number, row: YywwListItem): number {
    return row.id;
  }

  trackByScheduleGroupId(_: number, row: ScheduleGroupListItem): number {
    return row.id;
  }

  trackByYear(_: number, year: number): number {
    return year;
  }

  showToast(kind: ToastKind, message: string): void {
    this.toastKind = kind;
    this.toastMessage = message;
    this.cdr.detectChanges();

    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.clearToast(), 3500);
  }

  clearToast(): void {
    this.toastMessage = null;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  async onYearChange(year: number): Promise<void> {
    this.selectedYear = Number(year);
    await this.loadWeeksForYear(this.selectedYear, null);
  }

  prevWeek(): void {
    const idx = this.selectedWeekIndex;
    if (idx <= 0) return;

    this.selectedWeek = this.weeks[idx - 1];
    this.buildWeekDaysFromSelectedWeek();
    this.loadWeek();
  }

  nextWeek(): void {
    const idx = this.selectedWeekIndex;
    if (idx < 0 || idx >= this.weeks.length - 1) return;

    this.selectedWeek = this.weeks[idx + 1];
    this.buildWeekDaysFromSelectedWeek();
    this.loadWeek();
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
    if (this.sidebarOpen) {
      void this.ensureScheduleGroupsLoaded();
    }
  }

  toggleEmployeeSelected(employeeId: number): void {
    if (this.selectedEmployeeIds.has(employeeId)) {
      this.selectedEmployeeIds.delete(employeeId);
      return;
    }

    this.selectedEmployeeIds.add(employeeId);
  }

  clearSelectedEmployees(): void {
    this.selectedEmployeeIds.clear();
  }

  isEmployeeSelected(employeeId: number): boolean {
    return this.selectedEmployeeIds.has(employeeId);
  }

  private async ensureScheduleGroupsLoaded(): Promise<void> {
    if (this.scheduleGroupsLoaded) return;

    this.isLoadingScheduleGroups = true;
    try {
      const groups = await firstValueFrom(
        this.service.getScheduleGroupsAll().pipe(
          catchError((err) => {
            console.error('Failed to load schedule groups', err);
            this.showToast('error', 'Failed to load templates');
            return of([] as ScheduleGroupListItem[]);
          }),
        ),
      );

      this.scheduleGroups = groups ?? [];
      this.scheduleGroupsLoaded = true;
    } finally {
      this.isLoadingScheduleGroups = false;
      this.cdr.detectChanges();
    }
  }

  private async loadWeeksForYear(year: number, preferContainingDate: Date | null): Promise<void> {
    this.isLoadingWeeks = true;
    this.errorMessage = null;

    try {
      const weeks = await firstValueFrom(
        this.service.getWeeksByYear(year).pipe(
          catchError((err) => {
            console.error('Failed to load weeks', err);
            this.showToast('error', 'Failed to load weeks');
            return of([] as YywwListItem[]);
          }),
        ),
      );

      this.weeks = weeks ?? [];

      const preferred = preferContainingDate ?? new Date();
      this.selectedWeek =
        this.service.findWeekContainingDate(this.weeks, preferred) ??
        this.weeks.find((w) => w.year === year && w.week === dayjs(preferred).isoWeek()) ??
        this.weeks[0] ??
        null;

      this.buildWeekDaysFromSelectedWeek();
      this.loadWeek();
    } finally {
      this.isLoadingWeeks = false;
    }
  }

  private buildWeekDaysFromSelectedWeek(): void {
    const w = this.selectedWeek;
    if (!w) {
      this.weekDays = [];
      return;
    }

    const start = dayjs(w.startDate, 'YYYY-MM-DD', true).startOf('day');
    const keys: WeekdayKey[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    this.weekDays = keys.map((key, i) => {
      const d = start.add(i, 'day');
      return {
        key,
        label: key,
        date: d.toDate(),
        ymd: d.format('YYYY-MM-DD'),
      };
    });

    this.templatePreviewCache.clear();
  }

  private loadWeek(forceRefresh = false): void {
    const week = this.selectedWeek;
    if (!week) {
      this.weekSchedules$ = of([]);
      this.weekSchedulesSnapshot = [];
      this.rebuildIndexes([]);
      this.isLoadingWeek = false;
      return;
    }

    this.cancelInlineEdits();
    this.isLoadingWeek = true;
    this.errorMessage = null;

    const source$ = forceRefresh
      ? this.service.refreshWeekSchedules(week)
      : this.service.getWeekSchedulesByWeek(week);

    this.weekSchedules$ = source$.pipe(
      tap((rows) => {
        this.weekSchedulesSnapshot = rows ?? [];
        this.rebuildIndexes(this.weekSchedulesSnapshot);
        this.isLoadingWeek = false;
      }),
      catchError((err) => {
        console.error('Failed to load week schedules', err);
        this.errorMessage = 'Failed to load schedules';
        this.showToast('error', 'Failed to load schedules');
        this.weekSchedulesSnapshot = [];
        this.rebuildIndexes([]);
        this.isLoadingWeek = false;
        return of([]);
      }),
    );
  }

  private rebuildIndexes(rows: EmployeeWeekSchedules[]): void {
    this.employeeById.clear();
    this.blockByAssignmentId.clear();

    for (const row of rows ?? []) {
      this.employeeById.set(row.id, row);

      for (const block of row.schedules ?? []) {
        if (block.assignmentId != null) {
          this.blockByAssignmentId.set(block.assignmentId, block);
        }
      }
    }
  }

  getEmployeeDisplayName(employeeId: number): string {
    const emp = this.employeeById.get(employeeId);
    if (!emp) return `Employee ${employeeId}`;

    const ln = emp.lastName ?? '';
    const fn = emp.firstName ?? '';
    const name = `${ln}, ${fn}`.trim().replace(/^,/, '').trim();

    return name ? `${name} (${employeeId})` : `Employee ${employeeId}`;
  }

  isTemplateLoading(scheduleGroupId: number): boolean {
    return this.loadingTemplateDetailsIds.has(scheduleGroupId);
  }

  toggleTemplateExpanded(scheduleGroupId: number): void {
    if (this.expandedTemplateIds.has(scheduleGroupId)) {
      this.expandedTemplateIds.delete(scheduleGroupId);
      return;
    }

    this.expandedTemplateIds.add(scheduleGroupId);
    void this.ensureTemplatePreview(scheduleGroupId);
  }

  isTemplateExpanded(scheduleGroupId: number): boolean {
    return this.expandedTemplateIds.has(scheduleGroupId);
  }

  getTemplatePreview(scheduleGroupId: number): TemplatePreviewRow[] {
    return this.templatePreviewCache.get(scheduleGroupId) ?? [];
  }

  isTemplateDayDroppable(dayYmd: string): boolean {
    const details = this.getDraggedTemplateDetailsSync();
    if (!details) return true;
    return this.hasTemplateScheduleForDay(details, dayYmd);
  }

  private getDraggedTemplateId(): number | null {
    return this.dragPayload?.kind === 'template' ? this.dragPayload.scheduleGroupId : null;
  }

  private getDraggedTemplateDetailsSync(): ScheduleGroupDetails | null {
    const id = this.getDraggedTemplateId();
    if (!id) return null;
    return this.scheduleGroupDetailsMemory.get(id) ?? null;
  }

  private hasTemplateScheduleForDay(details: ScheduleGroupDetails, dayYmd: string): boolean {
    return !!this.getTemplateDaySchedule(details, dayYmd);
  }

  private async ensureTemplatePreview(scheduleGroupId: number): Promise<void> {
    if (this.templatePreviewCache.has(scheduleGroupId)) return;

    const details = await this.getScheduleGroupDetailsCached(scheduleGroupId);
    const rows: TemplatePreviewRow[] = this.weekDays.map((d) => ({
      dayLabel: d.label,
      timeRange: '—',
      isOvernight: false,
    }));

    if (details?.scheduleWithDays?.length) {
      for (const d of this.weekDays) {
        const plan = this.getTemplateDaySchedule(details, d.ymd);
        const idx = this.weekDays.findIndex((x) => x.key === d.key);

        if (plan && idx >= 0) {
          rows[idx] = {
            dayLabel: d.label,
            timeRange: `${plan.startTime}–${plan.endTime}`,
            isOvernight: plan.isOvernight,
          };
        }
      }
    }

    this.templatePreviewCache.set(scheduleGroupId, rows);
    this.cdr.detectChanges();
  }

  private async getScheduleGroupDetailsCached(id: number): Promise<ScheduleGroupDetails | null> {
    const cached = this.scheduleGroupDetailsMemory.get(id);
    if (cached) return cached;

    const inflight = this.scheduleGroupDetailsInflight.get(id);
    if (inflight) return inflight;

    this.loadingTemplateDetailsIds.add(id);

    const request = firstValueFrom(
      this.service.getScheduleGroupById(id).pipe(
        catchError((err) => {
          console.error('Failed to load schedule group details', err);
          this.showToast('error', 'Failed to load template details');
          return of(null as ScheduleGroupDetails | null);
        }),
      ),
    )
      .then((details) => {
        if (details) {
          this.scheduleGroupDetailsMemory.set(id, details);
        }
        return details;
      })
      .finally(() => {
        this.loadingTemplateDetailsIds.delete(id);
        this.scheduleGroupDetailsInflight.delete(id);
        this.cdr.detectChanges();
      });

    this.scheduleGroupDetailsInflight.set(id, request);
    return request;
  }

  onTemplateDragStart(ev: DragEvent, scheduleGroupId: number): void {
    this.dragPayload = { kind: 'template', scheduleGroupId };
    this.isTemplateDragging = true;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;

    void this.getScheduleGroupDetailsCached(scheduleGroupId);

    try {
      ev.dataTransfer?.setData('text/plain', String(scheduleGroupId));
      if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'copy';
    } catch {
      // ignore
    }
  }

  @HostListener('document:dragend')
  onGlobalDragEnd(): void {
    this.isTemplateDragging = false;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;
    this.dragPayload = null;
  }

  onLaneDragEnter(ev: DragEvent, employeeId: number): void {
    ev.preventDefault();
    if (!this.isTemplateDragging) return;

    this.hoverEmployeeId = employeeId;
    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;

    if (this.hoverClearRaf != null) {
      cancelAnimationFrame(this.hoverClearRaf);
      this.hoverClearRaf = null;
    }
  }

  onLaneDragLeave(ev: DragEvent, employeeId: number): void {
    ev.preventDefault();
    if (!this.isTemplateDragging) return;

    if (this.hoverClearRaf != null) cancelAnimationFrame(this.hoverClearRaf);

    const clientX = ev.clientX;
    const clientY = ev.clientY;
    const currentTarget = ev.currentTarget as HTMLElement | null;

    this.hoverClearRaf = requestAnimationFrame(() => {
      this.hoverClearRaf = null;

      if (this.hoverEmployeeId !== employeeId) return;

      const elUnder = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      if (!elUnder) return;

      if (currentTarget && (currentTarget === elUnder || currentTarget.contains(elUnder))) return;

      const row = currentTarget?.closest('.grid-row');
      if (row && row.contains(elUnder)) return;

      this.hoverEmployeeId = null;
      this.hoverDayIndex = null;
    });
  }

  onLaneDragOver(ev: DragEvent): void {
    ev.preventDefault();
    if (!this.isTemplateDragging) return;

    if (this.hoverClearRaf != null) {
      cancelAnimationFrame(this.hoverClearRaf);
      this.hoverClearRaf = null;
    }

    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;

    const laneOrZone = ev.currentTarget as HTMLElement | null;
    if (!laneOrZone) return;

    const lane = laneOrZone.closest('.week-lane') as HTMLElement | null;
    const stripes = lane?.querySelector<HTMLElement>('.week-stripes');
    const rect = (stripes ?? lane ?? laneOrZone).getBoundingClientRect();

    const x = Math.min(Math.max(0, (ev.clientX ?? rect.left) - rect.left), rect.width);
    const hourIndex = Math.min(167, Math.max(0, Math.floor((x / Math.max(1, rect.width)) * 168)));
    this.hoverDayIndex = Math.min(6, Math.max(0, Math.floor(hourIndex / 24)));
  }

  onSelectedEmployeesDragEnter(ev: DragEvent): void {
    ev.preventDefault();
    if (!this.isTemplateDragging || !this.selectedEmployeeIds.size) return;

    this.isSelectedDropzoneHover = true;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
    this.hoverSelectedEmployeesDayIndex = null;
  }

  onSelectedEmployeesDragOver(ev: DragEvent): void {
    ev.preventDefault();
    if (!this.isTemplateDragging || !this.selectedEmployeeIds.size) return;

    this.isSelectedDropzoneHover = true;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
    this.hoverSelectedEmployeesDayIndex = null;

    try {
      if (ev.dataTransfer) {
        ev.dataTransfer.dropEffect = 'copy';
      }
    } catch {
      // ignore
    }
  }

  onSelectedEmployeesDragLeave(ev: DragEvent): void {
    ev.preventDefault();

    const currentTarget = ev.currentTarget as HTMLElement | null;
    const related = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;

    if (currentTarget && related && currentTarget.contains(related)) {
      return;
    }

    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;
  }

  onSelectedEmployeesDayDragEnter(ev: DragEvent, dayIndex: number): void {
    ev.preventDefault();
    if (!this.isTemplateDragging || !this.selectedEmployeeIds.size) return;

    this.isSelectedDropzoneHover = true;
    this.hoverSelectedEmployeesDayIndex = dayIndex;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
  }

  onSelectedEmployeesDayDragOver(ev: DragEvent, dayIndex: number): void {
    ev.preventDefault();
    if (!this.isTemplateDragging || !this.selectedEmployeeIds.size) return;

    this.isSelectedDropzoneHover = true;
    this.hoverSelectedEmployeesDayIndex = dayIndex;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;

    try {
      if (ev.dataTransfer) {
        ev.dataTransfer.dropEffect = 'copy';
      }
    } catch {
      // ignore
    }
  }

  onSelectedEmployeesDayDragLeave(ev: DragEvent, dayIndex: number): void {
    ev.preventDefault();

    const currentTarget = ev.currentTarget as HTMLElement | null;
    const related = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;

    if (currentTarget && related && currentTarget.contains(related)) {
      return;
    }

    if (this.hoverSelectedEmployeesDayIndex === dayIndex) {
      this.hoverSelectedEmployeesDayIndex = null;
    }
  }

  async onDropToSelectedEmployees(ev: DragEvent): Promise<void> {
    ev.preventDefault();

    const payload = this.dragPayload;

    this.dragPayload = null;
    this.isTemplateDragging = false;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;

    if (!payload || payload.kind !== 'template') return;

    await this.applyTemplateToSelectedEmployeesWeek(payload.scheduleGroupId);
  }

  async onDropToSelectedEmployeesDay(ev: DragEvent, dayYmd: string): Promise<void> {
    ev.preventDefault();

    const payload = this.dragPayload;

    this.dragPayload = null;
    this.isTemplateDragging = false;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;

    if (!payload || payload.kind !== 'template') return;

    await this.applyTemplateToSelectedEmployeesDay(payload.scheduleGroupId, dayYmd);
  }

  async onDropToWeekLane(ev: DragEvent, employeeId: number): Promise<void> {
    ev.preventDefault();

    const payload = this.dragPayload;
    this.dragPayload = null;
    this.isTemplateDragging = false;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;

    if (!payload || payload.kind !== 'template') return;

    const lane = ev.currentTarget as HTMLElement | null;
    if (!lane) return;

    const stripes = lane.querySelector<HTMLElement>('.week-stripes');
    const rect = (stripes ?? lane).getBoundingClientRect();

    const x = Math.min(Math.max(0, (ev.clientX ?? rect.left) - rect.left), rect.width);
    const hourIndex = Math.min(167, Math.max(0, Math.floor((x / Math.max(1, rect.width)) * 168)));
    const dayIndex = Math.min(6, Math.max(0, Math.floor(hourIndex / 24)));
    const dayYmd = this.weekDays?.[dayIndex]?.ymd;

    if (!dayYmd) return;

    await this.applyTemplateToSingleDay(employeeId, dayYmd, payload.scheduleGroupId);
  }

  async onDropToEmployee(ev: DragEvent, employeeId: number): Promise<void> {
    ev.preventDefault();

    const payload = this.dragPayload;
    this.dragPayload = null;
    this.isTemplateDragging = false;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;

    if (!payload || payload.kind !== 'template') return;

    await this.applyTemplateToEmployeeWeek(employeeId, payload.scheduleGroupId);
  }

  onExistingDragStart(block: AssignedScheduleBlock): void {
    this.dragPayload = {
      kind: 'existing',
      assignmentId: block.assignmentId,
      employeeId: block.employeeId,
    };
  }

  async onDropToDayCell(employeeId: number, dayYmd: string): Promise<void> {
    const payload = this.dragPayload;
    this.dragPayload = null;

    this.isTemplateDragging = false;
    this.hoverEmployeeId = null;
    this.hoverDayIndex = null;
    this.isSelectedDropzoneHover = false;
    this.hoverSelectedEmployeesDayIndex = null;

    if (!payload) return;

    if (payload.kind === 'template') {
      await this.applyTemplateToSingleDay(employeeId, dayYmd, payload.scheduleGroupId);
      return;
    }

    console.warn('Existing assignment drag/drop not wired yet', payload);
  }

  private async applyTemplateToSingleDay(
    employeeId: number,
    dayYmd: string,
    scheduleGroupId: number,
  ): Promise<void> {
    const details = await this.getScheduleGroupDetailsCached(scheduleGroupId);
    if (!details) return;

    const plan = this.getTemplateDaySchedule(details, dayYmd);
    if (!plan) {
      this.showToast('info', 'Template has no schedule for that day');
      return;
    }

    const defaults = this.getDefaultDeptShift(employeeId, dayYmd);

    this.clearEditQueueContext();
    this.openEditDraft({
      employeeId,
      ymd: dayYmd,
      startTime: plan.startTime,
      endTime: plan.endTime,
      isOvernight: plan.isOvernight,
      departmentId: defaults.departmentId,
      shiftId: plan.shiftId ?? defaults.shiftId,
      sourceScheduleGroupId: scheduleGroupId,
    });
  }

  private async applyTemplateToEmployeeWeek(
    employeeId: number,
    scheduleGroupId: number,
  ): Promise<void> {
    const details = await this.getScheduleGroupDetailsCached(scheduleGroupId);
    if (!details) return;

    const dayPlans = this.weekDays
      .map((d) => {
        const plan = this.getTemplateDaySchedule(details, d.ymd);
        return plan ? { ...plan, ymd: d.ymd } : null;
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    if (!dayPlans.length) {
      this.showToast('info', 'Template has no days for this week');
      return;
    }

    this.templateApplyQueue = dayPlans.map((p) => {
      const defaults = this.getDefaultDeptShift(employeeId, p.ymd);

      return {
        employeeId,
        ymd: p.ymd,
        startTime: p.startTime,
        endTime: p.endTime,
        isOvernight: p.isOvernight,
        departmentId: defaults.departmentId,
        shiftId: p.shiftId ?? defaults.shiftId,
        sourceScheduleGroupId: scheduleGroupId,
      };
    });

    this.templateApplyActive = true;
    this.editQueueMode = 'singleTemplate';
    this.editQueueTotalCount = this.templateApplyQueue.length;
    this.editQueueEmployeeIds = [employeeId];
    this.editQueueTitle = 'Assign Template';
    this.openNextTemplateQueueDraft();
  }

  async applyTemplateToSelectedEmployeesWeek(scheduleGroupId: number): Promise<void> {
    if (!this.selectedEmployeeIds.size) {
      this.showToast('info', 'Select at least one employee');
      return;
    }

    const details = await this.getScheduleGroupDetailsCached(scheduleGroupId);
    if (!details) return;

    const queue: Array<BulkTemplateQueueItem | null> = this.weekDays.map((d) => {
      const plan = this.getTemplateDaySchedule(details, d.ymd);
      if (!plan) return null;

      return {
        ymd: d.ymd,
        dayLabel: d.label,
        startTime: plan.startTime,
        endTime: plan.endTime,
        isOvernight: plan.isOvernight,
        shiftId: plan.shiftId,
        timeZoneId: plan.timeZoneId,
        employeeIds: Array.from(this.selectedEmployeeIds),
        sourceScheduleGroupId: scheduleGroupId,
        scope: 'templateSelectedWeek',
      };
    });

    const filteredQueue = queue.filter((x): x is BulkTemplateQueueItem => x !== null);

    if (!filteredQueue.length) {
      this.showToast('info', 'Template has no days for this week');
      return;
    }

    this.bulkTemplateApplyQueue = filteredQueue;
    this.bulkTemplateApplyActive = true;
    this.editQueueMode = 'bulkTemplate';
    this.editQueueTotalCount = this.bulkTemplateApplyQueue.length;
    this.editQueueEmployeeIds = Array.from(this.selectedEmployeeIds);
    this.editQueueTitle = 'Assign Template to Selected Employees';
    this.openNextBulkTemplateDraft();
  }

  async applyTemplateToSelectedEmployeesDay(
    scheduleGroupId: number,
    dayYmd: string,
  ): Promise<void> {
    if (!this.selectedEmployeeIds.size) {
      this.showToast('info', 'Select at least one employee');
      return;
    }

    const details = await this.getScheduleGroupDetailsCached(scheduleGroupId);
    if (!details) return;

    const plan = this.getTemplateDaySchedule(details, dayYmd);
    if (!plan) {
      this.showToast('info', 'Template has no schedule for that day');
      return;
    }

    const dayVm = this.weekDays.find((x) => x.ymd === dayYmd);

    this.bulkTemplateApplyQueue = [
      {
        ymd: dayYmd,
        dayLabel: dayVm?.label ?? dayYmd,
        startTime: plan.startTime,
        endTime: plan.endTime,
        isOvernight: plan.isOvernight,
        shiftId: plan.shiftId,
        timeZoneId: plan.timeZoneId ?? 'America/New_York',
        employeeIds: Array.from(this.selectedEmployeeIds),
        sourceScheduleGroupId: scheduleGroupId,
        scope: 'templateSelectedDay',
      },
    ];

    this.bulkTemplateApplyActive = true;
    this.editQueueMode = 'bulkTemplate';
    this.editQueueTotalCount = 1;
    this.editQueueEmployeeIds = Array.from(this.selectedEmployeeIds);
    this.editQueueTitle = 'Assign Template to Selected Employees';
    this.openNextBulkTemplateDraft();
  }

  async openManualBulkCreateForSelectedWeek(): Promise<void> {
    if (!this.selectedEmployeeIds.size) {
      this.showToast('info', 'Select at least one employee');
      return;
    }

    this.openBulkAssignDraft({
      title: `Create schedule for ${this.selectedEmployeeIds.size} selected employee${
        this.selectedEmployeeIds.size === 1 ? '' : 's'
      }`,
      scope: 'manualSelectedWeek',
      employeeIds: Array.from(this.selectedEmployeeIds),
      selectedDayIdx: this.weekDays.map(() => true),
      startTime: '06:00',
      endTime: '14:00',
      isOvernight: false,
      departmentId: null,
      shiftId: null,
      currentYmd: null,
      currentDayLabel: null,
      timeZoneId: 'America/New_York',
    });
  }

  async openManualBulkCreateForSelectedDays(dayYmds: string[]): Promise<void> {
    if (!this.selectedEmployeeIds.size) {
      this.showToast('info', 'Select at least one employee');
      return;
    }

    this.openBulkAssignDraft({
      title: `Create schedule for ${this.selectedEmployeeIds.size} selected employee${
        this.selectedEmployeeIds.size === 1 ? '' : 's'
      }`,
      scope: 'manualSelectedDays',
      employeeIds: Array.from(this.selectedEmployeeIds),
      selectedDayIdx: this.buildSelectedDayIdxFromYmds(dayYmds),
      startTime: '06:00',
      endTime: '14:00',
      isOvernight: false,
      departmentId: null,
      shiftId: null,
      currentYmd: null,
      currentDayLabel: null,
      timeZoneId: 'America/New_York',
    });
  }

  async openManualBulkCreateForSelectedDay(dayYmd: string): Promise<void> {
    if (!this.selectedEmployeeIds.size) {
      this.showToast('info', 'Select at least one employee');
      return;
    }

    this.openBulkAssignDraft({
      title: `Create schedule for ${this.selectedEmployeeIds.size} selected employee${
        this.selectedEmployeeIds.size === 1 ? '' : 's'
      }`,
      scope: 'manualSelectedDay',
      employeeIds: Array.from(this.selectedEmployeeIds),
      selectedDayIdx: this.buildSelectedDayIdxFromYmds([dayYmd]),
      startTime: '06:00',
      endTime: '14:00',
      isOvernight: false,
      departmentId: null,
      shiftId: null,
      currentYmd: dayYmd,
      currentDayLabel: this.weekDays.find((x) => x.ymd === dayYmd)?.label ?? dayYmd,
      timeZoneId: 'America/New_York',
    });
  }

  private openBulkAssignDraft(draft: BulkAssignDraft): void {
    this.bulkAssignDraft = {
      ...draft,
      employeeIds: [...draft.employeeIds],
      selectedDayIdx: [...draft.selectedDayIdx],
    };
  }

  private openNextBulkTemplateDraft(): void {
    if (!this.bulkTemplateApplyActive) return;

    const next = this.bulkTemplateApplyQueue.shift() ?? null;
    if (!next) {
      this.bulkTemplateApplyActive = false;
      this.clearEditQueueContext();
      this.editDraft = null;
      this.showToast('success', 'Template applied');
      return;
    }

    this.editQueueCurrentIndex = this.editQueueTotalCount - this.bulkTemplateApplyQueue.length;
    this.editQueueMode = 'bulkTemplate';
    this.editQueueEmployeeIds = [...next.employeeIds];
    this.editQueueTitle = 'Assign Template to Selected Employees';

    this.openEditDraft({
      employeeId: next.employeeIds[0] ?? 0,
      ymd: next.ymd,
      startTime: next.startTime,
      endTime: next.endTime,
      isOvernight: next.isOvernight,
      departmentId: null,
      shiftId: next.shiftId,
      sourceScheduleGroupId: next.sourceScheduleGroupId,
    });
  }

  private clearBulkTemplateQueue(): void {
    this.bulkTemplateApplyQueue = [];
    this.bulkTemplateApplyActive = false;
  }

  cancelBulkAssign(): void {
    if (this.isBulkSaving) return;
    this.bulkAssignDraft = null;
  }

  toggleBulkAssignDay(idx: number): void {
    const draft = this.bulkAssignDraft;
    if (!draft) return;

    const next = [...draft.selectedDayIdx];
    next[idx] = !next[idx];

    this.bulkAssignDraft = {
      ...draft,
      selectedDayIdx: next,
    };
  }

  saveBulkAssign(): void {
    void this.saveBulkAssignInternal();
  }

  private async saveBulkAssignInternal(): Promise<void> {
    const draft = this.bulkAssignDraft;
    if (!draft) return;

    if (!draft.departmentId || !draft.shiftId) {
      this.showToast('info', 'Select a Department and Shift');
      return;
    }

    const selectedDays = this.weekDays
      .map((d, i) => (draft.selectedDayIdx[i] ? d.ymd : null))
      .filter((x): x is string => !!x);

    if (!selectedDays.length) {
      this.showToast('info', 'Select at least one day');
      return;
    }

    const bodies: CreateUserAssignedScheduleRequest[] = draft.employeeIds.flatMap((employeeId) =>
      selectedDays.map((ymd) => ({
        employeeId,
        departmentId: draft.departmentId!,
        shiftId: draft.shiftId!,
        localStartTime: draft.startTime,
        localEndtime: draft.endTime,
        timeZoneId: draft.timeZoneId ?? 'America/New_York',
        assignedDate: ymd,
      })),
    );

    if (!bodies.length) {
      this.showToast('info', 'No valid schedule assignments to save');
      return;
    }

    this.isBulkSaving = true;

    this.service
      .createAssignedSchedules(bodies)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isBulkSaving = false)),
        catchError((err) => {
          console.error('Failed to save bulk schedules', err);
          const msg = this.getApiErrorMessage(err);
          this.errorMessage = msg;
          this.showToast('error', msg);
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (!res) return;

        this.showToast(
          'success',
          `Saved ${bodies.length} schedule${bodies.length === 1 ? '' : 's'}`,
        );
        this.bulkAssignDraft = null;
        this.loadWeek(true);
      });
  }

  private openNextTemplateQueueDraft(): void {
    if (!this.templateApplyActive) return;

    const next = this.templateApplyQueue.shift() ?? null;
    if (!next) {
      this.templateApplyActive = false;
      this.clearEditQueueContext();
      this.editDraft = null;
      this.showToast('success', 'Template applied');
      return;
    }

    this.editQueueCurrentIndex = this.editQueueTotalCount - this.templateApplyQueue.length;
    this.editQueueMode = 'singleTemplate';
    this.editQueueEmployeeIds = [next.employeeId];
    this.editQueueTitle = 'Assign Template';

    this.openEditDraft(next);
  }

  private clearTemplateQueue(): void {
    this.templateApplyActive = false;
    this.templateApplyQueue = [];
  }

  private clearEditQueueContext(): void {
    this.clearTemplateQueue();
    this.clearBulkTemplateQueue();
    this.editQueueMode = 'none';
    this.editQueueTotalCount = 0;
    this.editQueueCurrentIndex = 0;
    this.editQueueEmployeeIds = [];
    this.editQueueTitle = null;
  }

  private getTemplateDaySchedule(
    details: ScheduleGroupDetails,
    dayYmd: string,
  ): TemplateDayPlan | null {
    const dow = dayjs(dayYmd).format('dddd');
    const match = details.scheduleWithDays?.find((x) => x?.dayofWeek === dow);
    const shiftSchedule = match?.shiftSchedule;
    const schedule = shiftSchedule?.schedule;
    const shift = shiftSchedule?.shift;

    if (!schedule?.localStartTime || !schedule?.localEndTime) {
      return null;
    }

    return {
      startTime: this.hhmm(schedule.localStartTime),
      endTime: this.hhmm(schedule.localEndTime),
      isOvernight:
        !!schedule.isOverNight ||
        this.isOvernightByTimes(schedule.localStartTime, schedule.localEndTime),
      shiftId: shift?.id ?? null,
      shiftName: shift?.name ?? null,
      timeZoneId: schedule.timeZoneId ?? 'America/New_York',
    };
  }

  private buildSelectedDayIdxFromYmds(dayYmds: string[]): boolean[] {
    const set = new Set(dayYmds);
    return this.weekDays.map((d) => set.has(d.ymd));
  }

  onOpenContextMenu(
    ev: MouseEvent,
    employeeId: number,
    dayYmd: string,
    assignmentId?: number,
  ): void {
    ev.preventDefault();
    ev.stopPropagation();

    this.contextMenu = {
      open: true,
      x: ev.clientX,
      y: ev.clientY,
      employeeId,
      ymd: dayYmd,
      assignmentId,
    };
  }

  closeContextMenu(): void {
    this.contextMenu.open = false;
  }

  @HostListener('document:click')
  onDocClick(): void {
    if (this.contextMenu.open) this.closeContextMenu();
  }

  async openEditDraftFromContext(): Promise<void> {
    const cm = this.contextMenu;
    if (!cm.open) return;

    const employeeId = cm.employeeId;
    const dayYmd = cm.ymd;
    const assignmentId = cm.assignmentId;

    this.closeContextMenu();

    if (assignmentId == null) {
      this.clearEditQueueContext();
      this.openEditDraftWithDefaults({
        employeeId,
        ymd: dayYmd,
        startTime: '06:00',
        endTime: '14:00',
        isOvernight: false,
      });
      return;
    }

    const block = this.findBlock(employeeId, assignmentId);
    if (!block) return;

    const eff = this.getEffectiveBlock(block);
    const defaults = this.getDefaultDeptShift(employeeId, dayYmd);

    this.clearEditQueueContext();
    this.openEditDraft({
      employeeId: block.employeeId,
      ymd: dayYmd,
      startTime: dayjs(eff.startDate).format('HH:mm'),
      endTime: dayjs(eff.endDate).format('HH:mm'),
      isOvernight: !!eff.isOvernight,
      departmentId: block.departmentId ?? defaults.departmentId,
      shiftId: block.shiftId ?? defaults.shiftId,
      sourceAssignmentId: block.assignmentId,
    });
  }

  openDeleteFromContext(): void {
    const cm = this.contextMenu;
    if (!cm.open) return;

    const employeeId = cm.employeeId;
    const dayYmd = cm.ymd;
    const assignmentId = cm.assignmentId;

    this.closeContextMenu();

    if (assignmentId == null) {
      this.showToast('info', 'No schedule selected to delete');
      return;
    }

    const block = this.findBlock(employeeId, assignmentId);
    if (!block) {
      this.showToast('error', 'Unable to find schedule to delete');
      return;
    }

    this.deleteTarget = {
      assignmentId,
      employeeId,
      ymd: dayYmd,
      label: this.getBlockLabelForWeek(block),
    };

    this.deleteConfirmOpen = true;
  }

  cancelDeleteSchedule(): void {
    if (this.isDeleting) return;
    this.deleteConfirmOpen = false;
    this.deleteTarget = null;
  }

  async confirmDeleteSchedule(): Promise<void> {
    const target = this.deleteTarget;
    if (!target?.assignmentId) return;

    this.isDeleting = true;

    try {
      await firstValueFrom(this.service.deleteAssignedSchedule(target.assignmentId));
      this.deleteConfirmOpen = false;
      this.deleteTarget = null;
      this.showToast('success', 'Schedule deleted');
      this.loadWeek(true);
    } catch (err) {
      console.error('Failed to delete assigned schedule', err);
      const msg = this.getApiErrorMessage(err);
      this.errorMessage = msg;
      this.showToast('error', msg);
    } finally {
      this.isDeleting = false;
      this.cdr.detectChanges();
    }
  }

  private findBlock(employeeId: number, assignmentId: number): AssignedScheduleBlock | null {
    const emp = this.employeeById.get(employeeId);
    return emp?.schedules?.find((s) => s.assignmentId === assignmentId) ?? null;
  }

  openEditDraft(draft: EditDraft): void {
    this.editDraft = { ...draft };
  }

  openEditDraftWithDefaults(args: {
    employeeId: number;
    ymd: string;
    startTime: string;
    endTime: string;
    isOvernight: boolean;
  }): void {
    const defaults = this.getDefaultDeptShift(args.employeeId, args.ymd);

    this.clearEditQueueContext();
    this.openEditDraft({
      employeeId: args.employeeId,
      ymd: args.ymd,
      startTime: args.startTime,
      endTime: args.endTime,
      isOvernight: args.isOvernight,
      departmentId: defaults.departmentId,
      shiftId: defaults.shiftId,
    });
  }

  cancelEdit(): void {
    this.editDraft = null;
    this.clearEditQueueContext();
    this.showToast('info', 'Edit cancelled');
  }

  saveEdit(): void {
    void this.saveEditInternal(
      this.templateApplyActive || this.bulkTemplateApplyActive ? 'next' : 'single',
    );
  }

  onModalNext(): void {
    void this.saveEditInternal(
      this.templateApplyActive || this.bulkTemplateApplyActive ? 'next' : 'single',
    );
  }

  onModalApplyAll(): void {
    void this.saveEditInternal('applyAll');
  }

  private async saveEditInternal(mode: SaveEditMode): Promise<void> {
    const d = this.editDraft;
    if (!d) return;

    if (!d.departmentId || !d.shiftId) {
      this.showToast('info', 'Select a Department and Shift');
      return;
    }

    let bodies: CreateUserAssignedScheduleRequest[] = [];

    if (this.bulkTemplateApplyActive) {
      const employeeIds = [...this.editQueueEmployeeIds];
      if (!employeeIds.length) {
        this.showToast('error', 'Missing selected employees for template assignment');
        return;
      }

      const currentBodies = employeeIds.map((employeeId) => ({
        employeeId,
        departmentId: d.departmentId!,
        shiftId: d.shiftId!,
        localStartTime: d.startTime,
        localEndtime: d.endTime,
        timeZoneId: 'America/New_York',
        assignedDate: d.ymd,
      }));

      if (mode === 'applyAll') {
        const remainingBodies = this.bulkTemplateApplyQueue.flatMap((item) =>
          item.employeeIds.map((employeeId) => ({
            employeeId,
            departmentId: d.departmentId!,
            shiftId: d.shiftId!,
            localStartTime: item.startTime,
            localEndtime: item.endTime,
            timeZoneId: item.timeZoneId ?? 'America/New_York',
            assignedDate: item.ymd,
          })),
        );

        bodies = [...currentBodies, ...remainingBodies];
      } else {
        bodies = currentBodies;
      }
    } else {
      const toBody = (draft: EditDraft): CreateUserAssignedScheduleRequest => ({
        employeeId: draft.employeeId,
        departmentId: draft.departmentId!,
        shiftId: draft.shiftId!,
        localStartTime: draft.startTime,
        localEndtime: draft.endTime,
        timeZoneId: 'America/New_York',
        assignedDate: draft.ymd,
      });

      if (mode === 'applyAll' && this.templateApplyActive) {
        const deptId = d.departmentId;
        const shiftId = d.shiftId;

        bodies = [{ ...d }, ...this.templateApplyQueue.map((x) => ({ ...x }))]
          .map((item) => ({
            ...item,
            departmentId: deptId,
            shiftId,
          }))
          .map(toBody);
      } else {
        bodies = [toBody(d)];
      }
    }

    if (!bodies.length) {
      this.showToast('info', 'No valid schedule assignments to save');
      return;
    }

    this.isSaving = true;

    this.service
      .createAssignedSchedules(bodies)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
        catchError((err) => {
          console.error('Failed to save schedule(s)', err);
          const msg = this.getApiErrorMessage(err);
          this.errorMessage = msg;
          this.showToast('error', msg);
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (!res) return;

        this.loadWeek(true);

        if (this.bulkTemplateApplyActive) {
          if (mode === 'applyAll') {
            this.showToast('success', `Applied to all (${bodies.length})`);
            this.editDraft = null;
            this.clearEditQueueContext();
            return;
          }

          this.showToast('success', 'Saved — next day');
          this.editDraft = null;
          Promise.resolve().then(() => this.openNextBulkTemplateDraft());
          return;
        }

        if (this.templateApplyActive) {
          if (mode === 'applyAll') {
            this.showToast('success', `Applied to all (${bodies.length})`);
            this.editDraft = null;
            this.clearEditQueueContext();
            return;
          }

          this.showToast('success', 'Saved — next day');
          this.editDraft = null;
          Promise.resolve().then(() => this.openNextTemplateQueueDraft());
          return;
        }

        this.showToast('success', 'Saved');
        this.editDraft = null;
        this.clearEditQueueContext();
      });
  }

  private getDefaultDeptShift(
    employeeId: number,
    dayYmd: string,
  ): { departmentId: number | null; shiftId: number | null } {
    const emp = this.employeeById.get(employeeId);
    const schedules = emp?.schedules ?? [];

    if (!schedules.length) return { departmentId: null, shiftId: null };

    const sameDay = schedules.find((b) => b.assignedDateYmd === dayYmd);
    if (sameDay?.departmentId || sameDay?.shiftId) {
      return {
        departmentId: sameDay.departmentId ?? null,
        shiftId: sameDay.shiftId ?? null,
      };
    }

    const any = schedules.find((b) => (b.departmentId ?? null) || (b.shiftId ?? null));
    return {
      departmentId: any?.departmentId ?? null,
      shiftId: any?.shiftId ?? null,
    };
  }

  private getWeekStart(): dayjs.Dayjs {
    const w = this.selectedWeek;
    return w
      ? dayjs(w.startDate, 'YYYY-MM-DD', true).startOf('day')
      : dayjs().startOf('isoWeek').startOf('day');
  }

  getEffectiveBlock(b: AssignedScheduleBlock): AssignedScheduleBlock {
    const draft = this.inlineDrafts.get(b.assignmentId);
    if (!draft) return b;

    return {
      ...b,
      startDate: draft.start,
      endDate: draft.end,
      isOvernight: dayjs(draft.end).startOf('day').isAfter(dayjs(draft.start).startOf('day')),
      departmentId: b.departmentId ?? draft.departmentId,
      shiftId: b.shiftId ?? draft.shiftId,
    };
  }

  getLeftPercentForWeek(b: AssignedScheduleBlock): number {
    const eff = this.getEffectiveBlock(b);
    const weekStart = this.getWeekStart();

    const startMin = dayjs(eff.startDate).diff(weekStart, 'minute');
    return (this.clamp(startMin, 0, this.WEEK_MINUTES) / this.WEEK_MINUTES) * 100;
  }

  getWidthPercentForWeek(b: AssignedScheduleBlock): number {
    const eff = this.getEffectiveBlock(b);
    const weekStart = this.getWeekStart();

    const startMin = dayjs(eff.startDate).diff(weekStart, 'minute');
    const endMin = dayjs(eff.endDate).diff(weekStart, 'minute');

    const a = this.clamp(startMin, 0, this.WEEK_MINUTES);
    const z = this.clamp(endMin, 0, this.WEEK_MINUTES);
    const widthMin = Math.max(0, z - a);

    return (widthMin / this.WEEK_MINUTES) * 100;
  }

  formatBlockRangeForWeek(b: AssignedScheduleBlock): string {
    const eff = this.getEffectiveBlock(b);
    const s = dayjs(eff.startDate);
    const e = dayjs(eff.endDate);

    if (s.isSame(e, 'day')) return `${s.format('HH:mm')} – ${e.format('HH:mm')}`;
    return `${s.format('ddd HH:mm')} – ${e.format('ddd HH:mm')}`;
  }

  enableInlineEdit(assignmentId: number, employeeId: number): void {
    const block = this.findBlock(employeeId, assignmentId);
    if (!block) return;

    this.inlineEditAssignmentId = assignmentId;

    if (!this.inlineDrafts.has(assignmentId)) {
      this.inlineDrafts.set(assignmentId, {
        assignmentId,
        employeeId,
        originalStart: new Date(block.startDate),
        originalEnd: new Date(block.endDate),
        start: new Date(block.startDate),
        end: new Date(block.endDate),
        departmentId: block.departmentId ?? null,
        shiftId: block.shiftId ?? null,
      });
    }
  }

  cancelInlineEdits(): void {
    this.inlineDrafts.clear();
    this.inlineEditAssignmentId = null;
    this.resetPointerDragState();
  }

  saveInlineEdits(): void {
    if (!this.inlineDrafts.size) return;

    const changes = Array.from(this.inlineDrafts.values()).filter((d) => {
      return (
        dayjs(d.start).valueOf() !== dayjs(d.originalStart).valueOf() ||
        dayjs(d.end).valueOf() !== dayjs(d.originalEnd).valueOf()
      );
    });

    if (!changes.length) {
      this.cancelInlineEdits();
      return;
    }

    const bodies: CreateUserAssignedScheduleRequest[] = [];

    for (const ch of changes) {
      const startYmd = dayjs(ch.start).format('YYYY-MM-DD');

      const inferred =
        (ch.departmentId && ch.shiftId
          ? { departmentId: ch.departmentId, shiftId: ch.shiftId }
          : this.inferDeptShiftForDay(ch.employeeId, startYmd, ch.assignmentId)) ?? null;

      if (!inferred) {
        const msg = `Inline save failed: missing Dept/Shift for ${this.getEmployeeDisplayName(
          ch.employeeId,
        )} on ${startYmd}.`;
        this.errorMessage = msg;
        this.showToast('error', msg);
        return;
      }

      bodies.push({
        employeeId: ch.employeeId,
        departmentId: inferred.departmentId,
        shiftId: inferred.shiftId,
        localStartTime: dayjs(ch.start).format('HH:mm'),
        localEndtime: dayjs(ch.end).format('HH:mm'),
        timeZoneId: 'America/New_York',
        assignedDate: startYmd,
      });
    }

    this.isSaving = true;

    this.service
      .createAssignedSchedules(bodies)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
        catchError((err) => {
          console.error('Failed to save inline schedules', err);
          const msg = this.getApiErrorMessage(err);
          this.errorMessage = msg;
          this.showToast('error', msg);
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (!res) return;

        this.showToast('success', `Saved ${bodies.length} change${bodies.length === 1 ? '' : 's'}`);
        this.cancelInlineEdits();
        this.loadWeek(true);
      });
  }

  onInlineHandlePointerDown(
    ev: PointerEvent,
    b: AssignedScheduleBlock,
    mode: InlineDragMode,
  ): void {
    if (this.inlineEditAssignmentId !== b.assignmentId) return;

    ev.preventDefault();
    ev.stopPropagation();

    const draft = this.inlineDrafts.get(b.assignmentId);
    if (!draft) return;

    this.beginPointerDrag(ev, b.assignmentId, mode);
  }

  onInlineBlockPointerDown(ev: PointerEvent, b: AssignedScheduleBlock): void {
    if (this.inlineEditAssignmentId !== b.assignmentId) return;

    ev.preventDefault();
    ev.stopPropagation();

    const draft = this.inlineDrafts.get(b.assignmentId);
    if (!draft) return;

    this.beginPointerDrag(ev, b.assignmentId, 'move');
  }

  private beginPointerDrag(ev: PointerEvent, assignmentId: number, mode: InlineDragMode): void {
    const lane = (ev.target as HTMLElement | null)?.closest('.week-lane') as HTMLElement | null;
    if (!lane) return;

    const draft = this.inlineDrafts.get(assignmentId);
    if (!draft) return;

    try {
      lane.setPointerCapture(ev.pointerId);
    } catch {
      // ignore
    }

    const weekStart = this.getWeekStart();
    this.activePointerId = ev.pointerId;
    this.activeMode = mode;
    this.activeAssignmentId = assignmentId;
    this.laneRect = lane.getBoundingClientRect();
    this.dragStartClientX = ev.clientX;
    this.dragStartStartMin = dayjs(draft.start).diff(weekStart, 'minute');
    this.dragStartEndMin = dayjs(draft.end).diff(weekStart, 'minute');
  }

  @HostListener('document:pointercancel', ['$event'])
  onDocPointerCancel(ev: PointerEvent): void {
    if (this.activePointerId == null || ev.pointerId !== this.activePointerId) return;
    this.resetPointerDragState();
  }

  @HostListener('document:pointermove', ['$event'])
  onDocPointerMove(ev: PointerEvent): void {
    if (
      this.activePointerId == null ||
      this.activeAssignmentId == null ||
      this.activeMode == null ||
      !this.laneRect
    ) {
      return;
    }

    if (ev.pointerId !== this.activePointerId) return;

    const draft = this.inlineDrafts.get(this.activeAssignmentId);
    if (!draft) return;

    const dx = ev.clientX - this.dragStartClientX;
    const minutesPerPx = this.WEEK_MINUTES / Math.max(1, this.laneRect.width);
    const deltaMin = this.snapMinutes(dx * minutesPerPx);

    let newStart = this.dragStartStartMin;
    let newEnd = this.dragStartEndMin;

    if (this.activeMode === 'move') {
      const duration = newEnd - newStart;
      newStart += deltaMin;
      newEnd = newStart + duration;

      if (newStart < 0) {
        newStart = 0;
        newEnd = duration;
      }

      if (newEnd > this.WEEK_MINUTES) {
        newEnd = this.WEEK_MINUTES;
        newStart = newEnd - duration;
      }
    }

    if (this.activeMode === 'resizeStart') {
      newStart = this.clamp(newStart + deltaMin, 0, newEnd - this.MIN_DURATION_MINUTES);
    }

    if (this.activeMode === 'resizeEnd') {
      newEnd = this.clamp(
        newEnd + deltaMin,
        newStart + this.MIN_DURATION_MINUTES,
        this.WEEK_MINUTES,
      );
    }

    const weekStart = this.getWeekStart();
    draft.start = weekStart.add(newStart, 'minute').toDate();
    draft.end = weekStart.add(newEnd, 'minute').toDate();
  }

  @HostListener('document:pointerup', ['$event'])
  onDocPointerUp(ev: PointerEvent): void {
    if (this.activePointerId == null || ev.pointerId !== this.activePointerId) return;
    this.resetPointerDragState();
  }

  private resetPointerDragState(): void {
    this.activePointerId = null;
    this.activeMode = null;
    this.activeAssignmentId = null;
    this.laneRect = null;
    this.dragStartClientX = 0;
    this.dragStartStartMin = 0;
    this.dragStartEndMin = 0;
  }

  private readonly onPanPointerDown = (ev: PointerEvent) => {
    if (!this.isPrimaryPointer(ev) || ev.button !== 0) return;

    const host = this.timelineScrollEl;
    if (!host) return;

    const target = ev.target as HTMLElement | null;

    if (target?.closest('button, a, input, select, textarea, label')) return;
    if (target?.closest('.resize-handle')) return;
    if (target?.closest('.schedule-block') && this.inlineEditAssignmentId != null) return;

    this.isPanning = true;
    this.panPointerId = ev.pointerId;
    this.panStartX = ev.clientX;
    this.panStartScrollLeft = host.scrollLeft;

    try {
      host.setPointerCapture(ev.pointerId);
    } catch {
      // ignore
    }

    host.classList.add('panning');
    ev.preventDefault();
  };

  private readonly onPanPointerMove = (ev: PointerEvent) => {
    if (!this.isPanning || this.panPointerId == null || ev.pointerId !== this.panPointerId) return;

    const host = this.timelineScrollEl;
    if (!host) return;

    const dx = ev.clientX - this.panStartX;
    host.scrollLeft = this.panStartScrollLeft - dx;
    ev.preventDefault();
  };

  private readonly onPanPointerUp = (ev: PointerEvent) => {
    if (this.panPointerId == null || ev.pointerId !== this.panPointerId) return;
    this.endPan();
  };

  private readonly onPanPointerCancel = (ev: PointerEvent) => {
    if (this.panPointerId == null || ev.pointerId !== this.panPointerId) return;
    this.endPan();
  };

  private bindHorizontalPanHandlers(): void {
    const host = this.timelineScrollEl;
    if (!host) return;

    host.addEventListener('pointerdown', this.onPanPointerDown, { passive: false });
    host.addEventListener('pointermove', this.onPanPointerMove, { passive: false });
    host.addEventListener('pointerup', this.onPanPointerUp, { passive: true });
    host.addEventListener('pointercancel', this.onPanPointerCancel, { passive: true });
  }

  private unbindHorizontalPanHandlers(): void {
    const host = this.timelineScrollEl;
    if (!host) return;

    host.removeEventListener('pointerdown', this.onPanPointerDown);
    host.removeEventListener('pointermove', this.onPanPointerMove);
    host.removeEventListener('pointerup', this.onPanPointerUp);
    host.removeEventListener('pointercancel', this.onPanPointerCancel);
  }

  private endPan(): void {
    const host = this.timelineScrollEl;
    this.isPanning = false;
    this.panPointerId = null;
    host?.classList.remove('panning');
  }

  private isPrimaryPointer(ev: PointerEvent): boolean {
    return ev.isPrimary ?? true;
  }

  private hhmm(hms: string): string {
    const s = String(hms ?? '').trim();
    return s ? s.slice(0, 5) : '';
  }

  private isOvernightByTimes(startHms: string, endHms: string): boolean {
    const a = this.hhmm(startHms);
    const b = this.hhmm(endHms);
    if (!a || !b) return false;
    return this.hhmmToMinutes(b) <= this.hhmmToMinutes(a);
  }

  private hhmmToMinutes(hhmm: string): number {
    const [hh, mm] = (hhmm ?? '').split(':').map((x) => Number(x));
    return (Number.isFinite(hh) ? hh : 0) * 60 + (Number.isFinite(mm) ? mm : 0);
  }

  private snapMinutes(min: number): number {
    return Math.round(min / this.SNAP_MINUTES) * this.SNAP_MINUTES;
  }

  private clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, n));
  }

  private inferDeptShiftForDay(
    employeeId: number,
    dayYmd: string,
    preferBlockAssignmentId?: number,
  ): { departmentId: number; shiftId: number } | null {
    const emp = this.employeeById.get(employeeId);
    if (!emp) return null;

    const blocks = emp.schedules ?? [];

    if (preferBlockAssignmentId != null) {
      const preferred = blocks.find((x) => x.assignmentId === preferBlockAssignmentId);
      if (preferred?.departmentId && preferred?.shiftId) {
        return {
          departmentId: preferred.departmentId,
          shiftId: preferred.shiftId,
        };
      }
    }

    const sameDay = blocks.find((x) => x.assignedDateYmd === dayYmd && x.departmentId && x.shiftId);
    if (sameDay?.departmentId && sameDay?.shiftId) {
      return {
        departmentId: sameDay.departmentId,
        shiftId: sameDay.shiftId,
      };
    }

    const any = blocks.find((x) => x.departmentId && x.shiftId);
    if (any?.departmentId && any?.shiftId) {
      return {
        departmentId: any.departmentId,
        shiftId: any.shiftId,
      };
    }

    return null;
  }

  private getDeptNameById(deptId: number | null | undefined): string | null {
    if (!deptId) return null;
    return this.filterService.filterValues?.departments.find((x) => x.id === deptId)?.name ?? null;
  }

  private getShiftNameById(shiftId: number | null | undefined): string | null {
    if (!shiftId) return null;
    return this.filterService.filterValues?.shifts.find((x) => x.id === shiftId)?.name ?? null;
  }

  getBlockLabelForWeek(b: AssignedScheduleBlock): string {
    const time = this.formatBlockRangeForWeek(b);

    const deptName =
      b.departmentName ??
      this.getDeptNameById(b.departmentId) ??
      (b.departmentId ? `Dept ${b.departmentId}` : null);

    const shiftName =
      b.shiftName ?? this.getShiftNameById(b.shiftId) ?? (b.shiftId ? `Shift ${b.shiftId}` : null);

    return [time, deptName, shiftName].filter(Boolean).join(' • ');
  }

  private getApiErrorMessage(err: any): string {
    const fromBody =
      (typeof err?.error === 'string' ? err.error : null) ??
      err?.error?.message ??
      err?.error?.error ??
      err?.message;

    if (fromBody && typeof fromBody === 'string') return fromBody;

    const errors = err?.error?.errors;
    if (errors && typeof errors === 'object') {
      const firstKey = Object.keys(errors)[0];
      const first = firstKey ? errors[firstKey]?.[0] : null;
      if (typeof first === 'string' && first.trim()) return first;
    }

    return 'Something went wrong.';
  }

  async openQuickAddForEmployee(employeeId: number): Promise<void> {
    const weekStartYmd = this.weekDays?.[0]?.ymd ?? dayjs().startOf('isoWeek').format('YYYY-MM-DD');
    const defaults = this.getDefaultDeptShift(employeeId, weekStartYmd);

    this.quickAddDraft = {
      employeeId,
      startTime: '06:00',
      endTime: '14:00',
      departmentId: defaults.departmentId,
      shiftId: defaults.shiftId,
      selectedDayIdx: Array.from({ length: 7 }, (_, i) => i < 5),
    };
  }

  cancelQuickAdd(): void {
    this.quickAddDraft = null;
  }

  toggleQuickAddDay(idx: number): void {
    const draft = this.quickAddDraft;
    if (!draft) return;

    const next = [...draft.selectedDayIdx];
    next[idx] = !next[idx];
    this.quickAddDraft = { ...draft, selectedDayIdx: next };
  }

  async saveQuickAdd(): Promise<void> {
    const d = this.quickAddDraft;
    if (!d) return;

    if (!d.departmentId || !d.shiftId) {
      this.showToast('info', 'Select a Department and Shift');
      return;
    }

    const selectedIdx = (d.selectedDayIdx ?? [])
      .map((value, index) => (value ? index : -1))
      .filter((index) => index >= 0);

    if (!selectedIdx.length) {
      this.showToast('info', 'Select at least one day');
      return;
    }

    const bodies: CreateUserAssignedScheduleRequest[] = selectedIdx
      .map((i) => this.weekDays?.[i]?.ymd)
      .filter((ymd): ymd is string => !!ymd)
      .map((ymd) => ({
        employeeId: d.employeeId,
        departmentId: d.departmentId!,
        shiftId: d.shiftId!,
        localStartTime: d.startTime,
        localEndtime: d.endTime,
        timeZoneId: 'America/New_York',
        assignedDate: ymd,
      }));

    if (!bodies.length) {
      this.showToast('info', 'No valid days selected for this week');
      return;
    }

    this.isQuickAddSaving = true;

    this.service
      .createAssignedSchedules(bodies)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isQuickAddSaving = false)),
        catchError((err) => {
          console.error('Failed to quick add schedules', err);
          const msg = this.getApiErrorMessage(err);
          this.errorMessage = msg;
          this.showToast('error', msg);
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (!res) return;

        this.showToast(
          'success',
          `Saved ${bodies.length} schedule${bodies.length === 1 ? '' : 's'}`,
        );
        this.quickAddDraft = null;
        this.cdr.detectChanges();
        this.loadWeek(true);
      });
  }
}
